/**
 * Account → Help — AI help bot, problem tickets, cash-out requests.
 * Notifies games@grudgestudio.org (staff review; no auto ledger drain).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FLEET } from "@shared/fleet";
import {
  Banknote,
  Bot,
  CircleHelp,
  Loader2,
  Mail,
  Send,
  Ticket,
} from "lucide-react";
import { useDappSign } from "@/contexts/dapp-sign-context";
import { signedBodyFields, type SignedChallenge } from "@/lib/signed-action";

const SUPPORT = FLEET.supportEmail || "games@grudgestudio.org";

type ChatMsg = { role: "user" | "assistant"; content: string; source?: string };

export function AccountHelpPanel({
  wallet,
  playGbux = 0,
}: {
  wallet: string | null;
  playGbux?: number;
}) {
  const { requestSign } = useDappSign();
  const [messages, setMessages] = useState<ChatMsg[]>([
    {
      role: "assistant",
      content:
        `Hi — I’m **BUDB Help**. Ask about deposits, play ledger, tables, slots, or BJ.\n\n` +
        `**Cash out** of play GBUX is staff-reviewed for now — submit a request and we notify **${SUPPORT}**.\n\n` +
        `Your play balance: **${Number(playGbux).toLocaleString()} GBUX**.`,
      source: "faq",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticketEmail, setTicketEmail] = useState("");
  const [ticketBody, setTicketBody] = useState("");
  const [cashoutAmt, setCashoutAmt] = useState("");
  const [cashoutNote, setCashoutNote] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [tickets, setTickets] = useState<
    Array<{
      id: string;
      category: string;
      subject: string;
      amount_gbux?: number;
      status: string;
      created_at: string;
      emailed?: number;
    }>
  >([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const headers = useCallback(() => {
    const h: Record<string, string> = { "Content-Type": "application/json" };
    if (wallet) h["X-Wallet-Address"] = wallet;
    return h;
  }, [wallet]);

  const loadTickets = useCallback(async () => {
    if (!wallet) return;
    try {
      const r = await fetch(
        `/api/support/tickets?wallet=${encodeURIComponent(wallet)}`,
        { headers: headers() },
      );
      if (!r.ok) return;
      const j = await r.json();
      setTickets(j.tickets || []);
    } catch {
      /* ignore */
    }
  }, [wallet, headers]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function sendChat(text?: string) {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    setStatus(null);
    try {
      const history = messages.slice(-6).map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const r = await fetch("/api/ai/help", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ message, wallet, history }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Help failed");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: j.reply || "No reply",
          source: j.source,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Help is temporarily unavailable. Email **" +
            SUPPORT +
            "** or try again. " +
            (e instanceof Error ? e.message : ""),
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  async function submitTicket(kind: "ticket" | "cashout") {
    setBusy(true);
    setStatus(null);
    try {
      const path =
        kind === "cashout" ? "/api/support/cashout" : "/api/support/ticket";
      if (kind === "ticket" && !String(ticketBody).trim()) {
        throw new Error("Describe the problem first");
      }

      let body: Record<string, unknown>;
      if (kind === "cashout") {
        if (!wallet || wallet.length < 32) {
          throw new Error("Connect a wallet to request cash out");
        }
        const amountGbux = cashoutAmt ? Number(cashoutAmt) : undefined;
        const signed = await requestSign({
          purpose: "cashout",
          action: "cashout",
          params:
            amountGbux != null && Number.isFinite(amountGbux) && amountGbux > 0
              ? { amountGbux }
              : undefined,
          details: [
            {
              label: "Amount",
              value:
                amountGbux != null && amountGbux > 0
                  ? `${amountGbux.toLocaleString()} GBUX`
                  : "Full play balance (staff review)",
            },
            {
              label: "Wallet",
              value: `${wallet.slice(0, 4)}…${wallet.slice(-4)}`,
            },
          ],
        });
        const fields = signedBodyFields({
          message: signed.message,
          signature: signed.signature,
          nonce: signed.nonce || "",
          action: "cashout",
        } as SignedChallenge);
        body = {
          wallet,
          email: ticketEmail || undefined,
          amountGbux,
          ticketBody:
            cashoutNote ||
            `Cash out request for play ledger GBUX. Wallet ${wallet}.`,
          message:
            cashoutNote ||
            `Cash out request for play ledger GBUX. Wallet ${wallet}.`,
          subject: "Cash out request",
          ...fields,
        };
      } else {
        body = {
          wallet,
          email: ticketEmail || undefined,
          category: "problem",
          subject: "Problem report",
          message: ticketBody,
        };
      }
      const r = await fetch(path, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Submit failed");
      setStatus(
        `Ticket ${j.ticketId?.slice?.(0, 8) || j.ticketId} · ${
          j.emailed ? `emailed ${SUPPORT}` : j.emailNote || "saved for staff"
        }`,
      );
      setTicketBody("");
      setCashoutNote("");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            (j.message || "Submitted.") +
            `\n\nTicket id: \`${j.ticketId}\`\nSupport: ${SUPPORT}`,
          source: "ticket",
        },
      ]);
      void loadTickets();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Submit failed");
    } finally {
      setBusy(false);
    }
  }

  const quick = [
    "How do I deposit GBUX?",
    "How do cash outs work?",
    "Play wallet vs on-chain?",
    "Report a stuck table",
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <Card className="border-violet-500/30 bg-zinc-950/90 lg:col-span-3">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-violet-200">
            <Bot className="h-4 w-4" />
            Help chat
            <Badge className="ml-auto bg-violet-900/50 text-[10px] font-normal">
              BUDBai help
            </Badge>
          </CardTitle>
          <p className="text-xs text-zinc-500 font-normal">
            Cash-out and play↔outside swap are staff email only:{" "}
            <a
              className="text-amber-300 underline"
              href={`mailto:${SUPPORT}?subject=BUDB%20cash-out%20/%20swap`}
            >
              {SUPPORT}
            </a>
            . In-app Send is play ledger → play ledger only.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {quick.map((q) => (
              <button
                key={q}
                type="button"
                disabled={busy}
                onClick={() => void sendChat(q)}
                className="rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1 text-[11px] text-zinc-300 hover:border-violet-500/50 hover:text-violet-100"
              >
                {q}
              </button>
            ))}
          </div>
          <div className="h-72 space-y-3 overflow-y-auto rounded-lg border border-zinc-800 bg-black/40 p-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-6 rounded-lg bg-amber-900/30 border border-amber-500/20 px-3 py-2 text-amber-50"
                    : "mr-4 rounded-lg bg-zinc-900/80 border border-zinc-800 px-3 py-2 text-zinc-200"
                }`}
              >
                {m.content}
                {m.source && m.role === "assistant" && (
                  <div className="mt-1 text-[10px] text-zinc-600">
                    source: {m.source}
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about deposits, cash out, rules…"
              className="bg-zinc-900 border-zinc-700"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void sendChat();
                }
              }}
              disabled={busy}
            />
            <Button
              className="bg-violet-700 hover:bg-violet-600 shrink-0"
              disabled={busy || !input.trim()}
              onClick={() => void sendChat()}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4 lg:col-span-2">
        <Card className="border-amber-500/30 bg-amber-950/15">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-amber-200">
              <Banknote className="h-4 w-4" />
              Cash out / swap (email staff)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-xs text-zinc-400">
              Play ledger:{" "}
              <strong className="text-emerald-300">
                {Number(playGbux).toLocaleString()} GBUX
              </strong>
              . Leaving the app (cash out or swap play → chain/SOL) is{" "}
              <strong className="text-amber-200">not automatic</strong> — ticket
              notifies <span className="text-amber-200">{SUPPORT}</span>. Player
              transfers stay server-side only (Send tab).
            </p>
            <a
              className="inline-flex text-xs text-amber-300 underline underline-offset-2"
              href={`mailto:${SUPPORT}?subject=BUDB%20cash-out%20/%20swap&body=Wallet:%20${encodeURIComponent(wallet || "")}%0APlay%20GBUX:%20${Number(playGbux)}%0AAmount:%0ANote:%0A`}
            >
              Or email {SUPPORT} directly
            </a>
            <Input
              type="email"
              placeholder="Your contact email (recommended)"
              value={ticketEmail}
              onChange={(e) => setTicketEmail(e.target.value)}
              className="bg-zinc-900 border-zinc-700"
            />
            <Input
              type="number"
              min={0}
              placeholder={`Amount GBUX (blank = full ${Number(playGbux).toLocaleString()})`}
              value={cashoutAmt}
              onChange={(e) => setCashoutAmt(e.target.value)}
              className="bg-zinc-900 border-zinc-700"
            />
            <Textarea
              placeholder="Notes (wallet confirmed, preferred payout, etc.)"
              value={cashoutNote}
              onChange={(e) => setCashoutNote(e.target.value)}
              className="bg-zinc-900 border-zinc-700 min-h-[72px]"
            />
            <Button
              className="w-full bg-amber-600 hover:bg-amber-500"
              disabled={busy || !wallet}
              onClick={() => void submitTicket("cashout")}
            >
              <Mail className="mr-2 h-4 w-4" />
              Submit cash-out request
            </Button>
            {!wallet && (
              <p className="text-[11px] text-zinc-500">
                Connect a wallet so we can attach your play balance.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-zinc-700 bg-zinc-950/90">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-zinc-200">
              <Ticket className="h-4 w-4" />
              Report a problem
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Textarea
              placeholder="What went wrong? Table id, time (UTC), steps…"
              value={ticketBody}
              onChange={(e) => setTicketBody(e.target.value)}
              className="bg-zinc-900 border-zinc-700 min-h-[100px]"
            />
            <Button
              variant="outline"
              className="w-full border-zinc-600"
              disabled={busy}
              onClick={() => void submitTicket("ticket")}
            >
              <CircleHelp className="mr-2 h-4 w-4" />
              Send to support
            </Button>
          </CardContent>
        </Card>

        {status && (
          <p className="text-xs text-emerald-300/90 border border-emerald-500/20 rounded-lg px-3 py-2 bg-emerald-950/30">
            {status}
          </p>
        )}

        {tickets.length > 0 && (
          <Card className="border-zinc-800 bg-zinc-950/80">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-zinc-400">
                Your recent tickets
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              {tickets.map((t) => (
                <div
                  key={t.id}
                  className="flex flex-wrap justify-between gap-1 rounded border border-zinc-800 px-2 py-1.5"
                >
                  <span className="font-mono text-zinc-500">
                    {t.id.slice(0, 8)}
                  </span>
                  <span className="text-amber-200/90">{t.category}</span>
                  <span className="text-zinc-500">{t.status}</span>
                  {t.amount_gbux != null && (
                    <span>{Number(t.amount_gbux).toLocaleString()} GBUX</span>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
